/**
 * Alternate phone helpers for Lookup Personal → Contact.
 * DB stores 11 digits with leading US country code (1XXXXXXXXXX).
 * UI shows fixed +1 prefix (outside control) and national mask (XXX) XXX-XXXX in the control.
 */

export function alternatePhoneDigitsOnly(v: string | null | undefined): string {
  if (v == null) return '';
  return String(v).replace(/\D/g, '');
}

/** Up to 10 national digits; strips leading 1 from 11-digit values; prefix-only "1" → empty. */
export function extractAlternatePhoneNationalDigits(display: string | null | undefined): string {
  let d = alternatePhoneDigitsOnly(display);
  if (d.length === 11 && d.startsWith('1')) d = d.slice(1);
  if (d === '1') return '';
  return d.slice(0, 10);
}

/** True when optional field has a complete 10-digit US number. */
export function hasCompleteAlternatePhone(display: string | null | undefined): boolean {
  return extractAlternatePhoneNationalDigits(display).length === 10;
}

/** DB payload: undefined if empty/incomplete; else 10 national digits (no country code). */
export function toAlternatePhoneDb(display: string | null | undefined): string | undefined {
  const national = extractAlternatePhoneNationalDigits(display);
  if (national.length !== 10) return undefined;
  return national;
}

/** National mask for form control from DB or user input (no +1 in value). */
export function fromAlternatePhoneDb(dbValue: string | null | undefined): string {
  if (!dbValue) return '';
  let d = alternatePhoneDigitsOnly(dbValue);
  if (d.length === 11 && d.startsWith('1')) d = d.slice(1);
  if (d.length !== 10) return '';
  return formatAlternatePhoneNationalMask(d);
}

/** Format 0–10 national digits as progressive (XXX) XXX-XXXX. */
export function formatAlternatePhoneNationalMask(nationalDigits: string): string {
  const digits = nationalDigits.slice(0, 10);
  if (digits.length === 0) return '';
  if (digits.length > 6) {
    return `(${digits.substring(0, 3)}) ${digits.substring(3, 6)}-${digits.substring(6)}`;
  }
  if (digits.length > 3) {
    return `(${digits.substring(0, 3)}) ${digits.substring(3)}`;
  }
  return `(${digits}`;
}

/** Sanitize arbitrary input (paste/type) into national mask. */
export function formatAlternatePhoneFromInput(input: string | null | undefined): string {
  const national = extractAlternatePhoneNationalDigits(input);
  return formatAlternatePhoneNationalMask(national);
}
